import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/Location';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const LOCATION_API_REDUCER_KEY = 'locations';
export const LOCATION_PATH = 'locations';

export const locationApi = createApi({
    reducerPath: LOCATION_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['Location', 'Locations'],
    endpoints: (builder) => ({
        getLocationList: builder.query<PaginationResult<Entity.Location>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ LOCATION_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['Locations'],
        }),
        getLocation: builder.query<Entity.Location, Entity.PrimaryLocation>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = locationNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ LOCATION_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['Location'],
        }),
        getLocationMultiple: builder.query<Entity.Location, Entity.PrimaryLocation[]>({
            query: (ids) => {
                return ({
                    url: `/${ LOCATION_PATH }/ids${Entity.PrimaryLocation.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['Locations'],
        }),
        getLocationPresentation: builder.query<PaginationResult<Entity.Location>, Entity.PresentationLocationRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ LOCATION_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['Locations'],
        }),
        getLocationAdvancedPresentation: builder.query<PaginationResult<Entity.Location>, Entity.FilterAdvancedPresentationRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
                return ({
                    url: `/${ LOCATION_PATH }/find/advancedPresentation?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                    data: request.filter,
                })
            },
            providesTags: ['Locations'],
        }),
        
        insertLocation: builder.mutation<Entity.Location, Entity.CreateLocation>({
            query: (toInsert) => {
                return ({
                    url: `/${ LOCATION_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    locationApi.util.upsertQueryData('getLocation', Entity.PrimaryLocation.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['Locations'],
        }),
        insertMultipleLocation: builder.mutation<Entity.Location[], Entity.CreateLocation[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ LOCATION_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            locationApi.util.upsertQueryData('getLocation', Entity.PrimaryLocation.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['Locations'],
        }),
        updateLocation: builder.mutation<Entity.Location, Entity.UpdateEntityLocation>({
            query: (toUpdate) => {
                return ({
                    url: `/${ LOCATION_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['Locations'],
        }),
        updateMultipleLocation: builder.mutation<Entity.Location[], Entity.UpdateEntityLocation[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ LOCATION_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['Locations'],
        }),
        deleteLocation: builder.mutation<Entity.Location, Entity.Location>({
            query: (toDelete) => {
                return ({
                    url: `/${ LOCATION_PATH }/${Entity.PrimaryLocation.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Locations'],
        }),
        deleteMultipleLocation: builder.mutation<Entity.Location[], Entity.PrimaryLocation[]>({
            query: (ids) => {
                return ({
                    url: `/${ LOCATION_PATH }/batch${Entity.PrimaryLocation.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Locations'],
        }),
    }),
})

export const [locationNormalizer, locationNormalizerMiddleware] = createNormalizationMiddleware(LOCATION_API_REDUCER_KEY, locationApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetLocationListQuery,
    useGetLocationMultipleQuery,
    useGetLocationPresentationQuery,
    useGetLocationQuery,
    useInsertLocationMutation,
    useInsertMultipleLocationMutation,
    useUpdateLocationMutation,
    useUpdateMultipleLocationMutation,
    useDeleteLocationMutation,
    useDeleteMultipleLocationMutation,
    useGetLocationAdvancedPresentationQuery,
    
} = locationApi
